import React, { useCallback, useContext, useState, useEffect } from 'react';
import { Modal, MoreDropDown } from '@components/common';
import { IPolicyVersion, IPolicy } from '../../types';
import EventContext from '../../EventContext';

interface IProps {
  policy: IPolicy;
  version: IPolicyVersion;
  canClone?: boolean;
}

enum ConfirmationType {
  Activate,
  Delete,
  Clone,
}

const Options: React.FC<IProps> = ({ policy, version, canClone = false }) => {
  const { deletePolicy, activatePolicy, clonePolicy, editPolicy } = useContext(EventContext);

  const [modalVisible, setModalVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmType, setConfirmType] = useState(ConfirmationType.Activate);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    if (modalVisible === false) {
      setConfirmLoading(false);
    }
  }, [modalVisible]);

  const onModalCancel = useCallback(() => {
    setModalVisible(false);
  }, [setModalVisible]);

  const handlers = {
    [ConfirmationType.Activate]: activatePolicy,
    [ConfirmationType.Delete]: deletePolicy,
    [ConfirmationType.Clone]: clonePolicy,
  };

  const onModalOk = () => {
    setConfirmLoading(true);
    handlers[confirmType](version.id);
  };

  const handleEdit = useCallback(() => {
    editPolicy(policy, version);
  }, [version, policy]);

  const showDeleteConfirm = useCallback(() => {
    setMessage(`Are you sure you want to delete ${version.name}?`);
    setModalVisible(true);
    setConfirmType(ConfirmationType.Delete);
  }, [version, setMessage, setModalVisible]);

  const showCloneConfirm = useCallback(() => {
    setMessage(`Are you sure you want to clone ${version.name}?`);
    setModalVisible(true);
    setConfirmType(ConfirmationType.Clone);
  }, [version, setMessage, setModalVisible]);

  const showActivationConfirm = useCallback(() => {
    setMessage(`Are you sure you want to activate "${version.name}" policy?`);
    setModalVisible(true);
    setConfirmType(ConfirmationType.Activate);
  }, [version, setMessage, setModalVisible]);

  return (
    <>
      <MoreDropDown>
        <span role="button" onClick={handleEdit}>
          Edit
        </span>
        {canClone && (
          <span role="button" onClick={showCloneConfirm}>
            Clone
          </span>
        )}
        {!version.is_active && (
          <span role="button" onClick={showDeleteConfirm}>
            Delete
          </span>
        )}
        {!version.is_active && (
          <span role="button" onClick={showActivationConfirm}>
            Activate
          </span>
        )}
      </MoreDropDown>
      <Modal
        title="Confirm"
        content={message}
        visible={modalVisible}
        onCancel={onModalCancel}
        onOk={onModalOk}
        confirmLoading={confirmLoading}
      />
    </>
  );
};

export default Options;
